import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Dialog,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import AppMenu from '../components/AppMenu';
import { useAuth } from '../contexts/AuthContext';
import { useSnackbar } from '../contexts/SnackbarContext';
import { getUserBookings, getUpcomingTickets, getPastTickets, getCancelledTickets, cancelUpcomingTicket, BookTicketResponse } from '../services/ticketService';
import { getEventById, EventResponse } from '../services/eventService';
import { Add as AddIcon, Event as EventIcon, Person as PersonIcon, CalendarToday as CalendarIcon, AccessTime as AccessTimeIcon, LocationOn as LocationIcon, Cancel as CancelIcon, Close as CloseIcon } from '@mui/icons-material';
import { PLATFORM_FEE_RATE } from '../config';
import { formatCurrency } from '../utils/currency';

interface BookingWithEvent extends BookTicketResponse {
  event?: EventResponse;
}

const roundToTwo = (value: number): number => Math.round(value * 100) / 100;

const normalizeAmount = (value: unknown): number | undefined => {
  if (value === null || value === undefined) return undefined;
  const numericValue = typeof value === 'string' ? Number(value) : (value as number);
  if (Number.isNaN(numericValue)) {
    return undefined;
  }
  return numericValue;
};

const deriveBookingBreakdown = (booking: BookTicketResponse) => {
  const currency = booking.currency ?? 'EUR';
  const totalFromServer = normalizeAmount(booking.totalAmount) ?? normalizeAmount(booking.totalPrice) ?? 0;

  const subtotalFromServer = normalizeAmount(booking.sellingPrice);

  const platformFeeRaw = booking.platformFee ?? booking.platfomFee;

  const platformFeeFromServer = normalizeAmount(platformFeeRaw);

  const subtotal = typeof subtotalFromServer === 'number'
    ? roundToTwo(subtotalFromServer * Math.max(booking.numberOfTickets ?? 1, 1))
    : roundToTwo(Math.max(totalFromServer - (platformFeeFromServer ?? 0), 0));

  const platformFee = typeof platformFeeFromServer === 'number'
    ? platformFeeFromServer
    : roundToTwo(Math.max(totalFromServer - subtotal, 0));

  const total = totalFromServer > 0 ? totalFromServer : roundToTwo(subtotal + platformFee);

  return {
    subtotal,
    platformFee,
    total,
    currency,
  };
};

const MyBookings = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [upcomingBookings, setUpcomingBookings] = useState<BookingWithEvent[]>([]);
  const [pastBookings, setPastBookings] = useState<BookingWithEvent[]>([]);
  const [cancelledBookings, setCancelledBookings] = useState<BookingWithEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [loadedTabs, setLoadedTabs] = useState<Set<number>>(new Set());
  const [cancellingTicketId, setCancellingTicketId] = useState<number | null>(null);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [bookingToCancel, setBookingToCancel] = useState<{ id: number; title: string } | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { showSnackbar } = useSnackbar();
  const isFetchingRef = useRef(false);

  useEffect(() => {
    if (isAuthenticated && !hasInitialized) {
      setHasInitialized(true);
      // Load the first tab (Upcoming) by default
      fetchTabData(0);
    } else if (!isAuthenticated) {
      setLoading(false);
      setHasInitialized(false);
      setLoadedTabs(new Set());
    }
  }, [isAuthenticated, hasInitialized]);

  const fetchTabData = async (tabIndex: number) => {
    // Prevent duplicate calls
    if (isFetchingRef.current) {
      return;
    }
    
    // Check if this tab has already been loaded
    if (loadedTabs.has(tabIndex)) {
      return;
    }
    
    try {
      isFetchingRef.current = true;
      setLoading(true);
      setError(null);
      
      let tickets: BookTicketResponse[];
      
      // Fetch data based on tab index
      switch (tabIndex) {
        case 0:
          tickets = await getUpcomingTickets();
          break;
        case 1:
          tickets = await getPastTickets();
          break;
        case 2:
          tickets = await getCancelledTickets();
          break;
        default:
          tickets = [];
      }
      
      // Helper function to attach event details to bookings
      const attachEventDetails = async (bookings: BookTicketResponse[]): Promise<BookingWithEvent[]> => {
        const eventIds = Array.from(new Set(bookings.map(booking => booking.eventId)));
        const eventsMap = new Map();
        
        await Promise.all(
          eventIds.map(async (eventId) => {
            try {
              const event = await getEventById(eventId.toString());
              eventsMap.set(eventId, event);
            } catch (err) {
              console.error(`Failed to fetch event ${eventId}:`, err);
              eventsMap.set(eventId, undefined);
            }
          })
        );
        
        return bookings.map(booking => ({
          ...booking,
          event: eventsMap.get(booking.eventId)
        }));
      };
      
      // Attach event details to bookings
      const bookingsWithEvents = await attachEventDetails(tickets);
      
      // Update the appropriate state based on tab
      switch (tabIndex) {
        case 0:
          setUpcomingBookings(bookingsWithEvents);
          break;
        case 1:
          setPastBookings(bookingsWithEvents);
          break;
        case 2:
          setCancelledBookings(bookingsWithEvents);
          break;
      }
      
      // Mark this tab as loaded
      setLoadedTabs(prev => new Set(prev).add(tabIndex));
      
    } catch (error) {
      console.error('Error fetching tab data:', error);
      setError('Failed to load bookings. Please try again.');
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  };

  const handleEventClick = (eventId: number) => {
    navigate(`/events/${eventId}`);
  };

  const handleCancelBookingClick = (bookingId: number, eventTitle: string) => {
    setBookingToCancel({ id: bookingId, title: eventTitle });
    setCancelModalOpen(true);
  };

  const handleCancelBooking = async () => {
    if (!bookingToCancel) return;

    try {
      setCancellingTicketId(bookingToCancel.id);
      await cancelUpcomingTicket(bookingToCancel.id);
      
      // Remove the cancelled booking from upcoming bookings
      setUpcomingBookings(prev => prev.filter(booking => booking.ticketId !== bookingToCancel.id));
      
      // Refresh cancelled bookings to show the newly cancelled booking
      if (loadedTabs.has(2)) {
        fetchTabData(2);
      }
      
      showSnackbar('Booking cancelled successfully', 'success');
      setCancelModalOpen(false);
      setBookingToCancel(null);
    } catch (error) {
      console.error('Error cancelling booking:', error);
      showSnackbar('Failed to cancel booking. Please try again.', 'error');
    } finally {
      setCancellingTicketId(null);
    }
  };

  const handleCancelModalClose = () => {
    setCancelModalOpen(false);
    setBookingToCancel(null);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    // Fetch data for the new tab if it hasn't been loaded yet
    if (!loadedTabs.has(newValue)) {
      fetchTabData(newValue);
    }
  };

  const getCurrentBookings = () => {
    switch (activeTab) {
      case 0:
        return upcomingBookings;
      case 1:
        return pastBookings;
      case 2:
        return cancelledBookings;
      default:
        return upcomingBookings;
    }
  };

  const getTabLabel = (index: number) => {
    const isLoaded = loadedTabs.has(index);
    const isLoading = loading && !isLoaded;
    
    switch (index) {
      case 0:
        return isLoading ? 'Upcoming...' : 'Upcoming';
      case 1:
        return isLoading ? 'Past...' : 'Past';
      case 2:
        return isLoading ? 'Cancelled...' : 'Cancelled';
      default:
        return 'Upcoming';
    }
  };

  const formatDate = (dateString: string | undefined | null) => {
    if (!dateString) {
      return 'Date not available';
    }
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return 'Invalid date';
    }
  };

  const formatTime = (dateString: string | undefined | null) => {
    if (!dateString) {
      return 'Time not available';
    }
    try {
      return new Date(dateString).toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      }).replace(/am/g, 'AM').replace(/pm/g, 'PM');
    } catch (error) {
      return 'Invalid time';
    }
  };

  const getDaysUntilEvent = (dateString: string | undefined | null) => {
    if (!dateString) return null;
    
    try {
      const eventDate = new Date(dateString);
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset time to start of day
      eventDate.setHours(0, 0, 0, 0); // Reset time to start of day
      
      const diffTime = eventDate.getTime() - today.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      return diffDays;
    } catch (error) {
      return null;
    }
  };



  if (!isAuthenticated) {
    return (
      <Box width="100vw" overflow="hidden">
        <AppMenu 
          onLoginClick={() => navigate('/login')} 
          onSignupClick={() => navigate('/register')} 
          currentPath={location.pathname}
        />
        
        <Container maxWidth="lg">
          <Box sx={{ p: 3, textAlign: 'center', mt: 4 }}>
            <Typography variant="h5" color="text.secondary" gutterBottom>
              Please log in to view your bookings
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate('/login')}
              sx={{ mt: 2 }}
            >
              Login
            </Button>
          </Box>
        </Container>
        

      </Box>
    );
  }

  return (
    <Box width="100vw" overflow="hidden">
      <AppMenu 
        onLoginClick={() => navigate('/login')} 
        onSignupClick={() => navigate('/register')} 
        currentPath={location.pathname}
      />
      
      <Container maxWidth="lg">
        <Box sx={{ p: 3 }}>
          <Typography 
            variant="h4" 
            component="h1"
            sx={{ 
              fontWeight: 700,
              color: 'primary.main',
              mb: 3,
              fontSize: { xs: '1.5rem', sm: '2rem', md: '2.25rem' }
            }}
          >
            My Bookings
          </Typography>

          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs value={activeTab} onChange={handleTabChange} aria-label="booking tabs">
              <Tab label={getTabLabel(0)} />
              <Tab label={getTabLabel(1)} />
              <Tab label={getTabLabel(2)} />
            </Tabs>
          </Box>

          {loading && !loadedTabs.has(activeTab) ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress size={60} thickness={4} />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          ) : getCurrentBookings().length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <EventIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                {activeTab === 0 && 'No upcoming bookings'}
                {activeTab === 1 && 'No past bookings'}
                {activeTab === 2 && 'No cancelled bookings'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {activeTab === 0 && 'You don\'t have any upcoming events booked.'}
                {activeTab === 1 && 'You don\'t have any past events.'}
                {activeTab === 2 && 'You don\'t have any cancelled bookings.'}
              </Typography>
              {activeTab === 0 && (
                <Button
                  variant="contained"
                  onClick={() => navigate('/events')}
                  startIcon={<AddIcon />}
                >
                  Browse Events
                </Button>
              )}
            </Paper>
          ) : (
            <Grid container spacing={3}>
              {getCurrentBookings().map((booking) => {
                const breakdown = deriveBookingBreakdown(booking);
                const hasPlatformFee = breakdown.platformFee > 0;
                return (
                  <Grid item xs={12} md={6} key={booking.ticketId}>
                  <Paper 
                    sx={{ 
                      p: 3, 
                      cursor: 'pointer',
                      '&:hover': {
                        boxShadow: 4,
                        transform: 'translateY(-2px)',
                        transition: 'all 0.2s ease-in-out'
                      }
                    }}
                    onClick={() => booking.event && handleEventClick(booking.event.id)}
                  >
                    {booking.event ? (
                      <>
                        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography 
                            variant="h6" 
                            component="h3" 
                            sx={{ 
                              fontWeight: 600,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {booking.event.title}
                          </Typography>
                          {(() => {
                            if (activeTab === 0) {
                              // Upcoming tab - show days until event
                              const daysUntil = getDaysUntilEvent(booking.event.startTime);
                              if (daysUntil !== null && daysUntil >= 0) {
                                return (
                                  <Chip
                                    label={daysUntil === 0 ? 'Today' : daysUntil === 1 ? 'Tomorrow' : `in ${daysUntil} days`}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                    sx={{
                                      fontSize: '0.7rem',
                                      height: 20,
                                      flexShrink: 0
                                    }}
                                  />
                                );
                              }
                            } else if (activeTab === 2) {
                              // Cancelled tab - show "Cancelled" chip
                              return (
                                <Chip
                                  label="Cancelled"
                                  size="small"
                                  color="error"
                                  variant="outlined"
                                  sx={{
                                    fontSize: '0.7rem',
                                    height: 20,
                                    flexShrink: 0
                                  }}
                                />
                              );
                            }
                            return null;
                          })()}
                        </Box>
                        
                        <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
                          {/* Event Details */}
                          <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                              <CalendarIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                              <Typography variant="body2" color="text.secondary">
                                {formatDate(booking.event.startTime)}
                              </Typography>
                            </Box>
                            
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                              <AccessTimeIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                              <Typography variant="body2" color="text.secondary">
                                {formatTime(booking.event.startTime)}
                              </Typography>
                            </Box>
                            
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                              <LocationIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                              <Typography 
                                variant="body2" 
                                color="text.secondary"
                                sx={{ 
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                {booking.event.venue || 'Venue not specified'}
                              </Typography>
                            </Box>
                            
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <PersonIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                              <Typography variant="body2" color="text.secondary">
                                {(booking.numberOfTickets || 1) === 1 
                                  ? '1 ticket' 
                                  : `${booking.numberOfTickets} tickets`}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>

                        <Box
                          sx={{
                            mt: 2,
                            p: 2,
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: 'divider',
                            bgcolor: 'background.default',
                          }}
                        >
                          <Box display="flex" justifyContent="space-between" mb={1}>
                            <Typography variant="body2" color="text.secondary">
                              Tickets subtotal
                            </Typography>
                            <Typography variant="body2" color="text.primary" fontWeight={600}>
                              {formatCurrency(breakdown.subtotal, breakdown.currency)}
                            </Typography>
                          </Box>
                          {hasPlatformFee && (
                            <Box display="flex" justifyContent="space-between" mb={1}>
                              <Typography variant="body2" color="text.secondary">
                                Platform fee ({Math.round(PLATFORM_FEE_RATE * 100)}%)
                              </Typography>
                              <Typography variant="body2" color="text.primary" fontWeight={600}>
                                {formatCurrency(breakdown.platformFee, breakdown.currency)}
                              </Typography>
                            </Box>
                          )}
                          <Box display="flex" justifyContent="space-between" pt={1} borderTop="1px dashed" borderColor="divider">
                            <Typography variant="body2" color="text.primary" fontWeight={600}>
                              {breakdown.total > 0 ? 'Total amount' : 'Total'}
                            </Typography>
                            <Typography variant="body2" color="text.primary" fontWeight={700}>
                              {breakdown.total > 0
                                ? formatCurrency(breakdown.total, breakdown.currency)
                                : 'FREE'}
                            </Typography>
                          </Box>
                        </Box>
                        {!hasPlatformFee && (
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                            No platform fee was applied to this booking.
                          </Typography>
                        )}

                        {/* Cancel Button - Only for upcoming bookings */}
                        {activeTab === 0 && (
                          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                            <Button
                              variant="contained"
                              color="error"
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation(); // Prevent card click
                                handleCancelBookingClick(booking.ticketId, booking.event?.title || 'this event');
                              }}
                              disabled={cancellingTicketId === booking.ticketId}
                              sx={{ 
                                textTransform: 'none',
                                fontSize: '0.875rem',
                                fontWeight: 600,
                                borderRadius: 2,
                                px: 2,
                                py: 0.5,
                                minWidth: 'auto',
                                '&:disabled': {
                                  backgroundColor: '#E0E0E0',
                                  color: '#9E9E9E',
                                }
                              }}
                            >
                              {cancellingTicketId === booking.ticketId ? 'Cancelling...' : 'Cancel'}
                            </Button>
                          </Box>
                        )}

                      </>
                    ) : (
                      <Box>
                        <Typography variant="h6" color="error" gutterBottom>
                          Event not found
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Event ID: {booking.eventId}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {(booking.numberOfTickets || 1) === 1 
                            ? '1 ticket' 
                            : `${booking.numberOfTickets} tickets`}
                        </Typography>
                      </Box>
                    )}
                  </Paper>
                </Grid>
                );
              })}
            </Grid>
          )}
        </Box>
      </Container>
      
      {/* Cancellation Confirmation Modal */}
      <Dialog
        open={cancelModalOpen}
        onClose={handleCancelModalClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
          }
        }}
      >
        <DialogContent sx={{ textAlign: 'center', py: 6, px: 4 }}>
          <Box
            sx={{
              width: 100,
              height: 100,
              borderRadius: '50%',
              bgcolor: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 3
            }}
          >
            <CancelIcon 
              sx={{ 
                fontSize: 60, 
                color: 'error.main'
              }} 
            />
          </Box>
          <Typography 
            variant="h5" 
            component="h2" 
            gutterBottom
            sx={{ 
              fontWeight: 700,
              color: 'primary.main',
              mb: 2
            }}
          >
            Cancel Booking
          </Typography>
          <Typography 
            variant="subtitle1" 
            color="text.secondary"
            sx={{ 
              fontSize: '1.1rem',
              lineHeight: 1.6,
              mb: 3
            }}
          >
            Are you sure you want to cancel your booking for <b>"{bookingToCancel?.title}"</b>? This action cannot be undone.
          </Typography>
          <Typography 
            variant="body2" 
            color="text.secondary"
            sx={{ 
              fontStyle: 'italic',
              mb: 2
            }}
          >
            You can always book again if the event is still available.
          </Typography>
          <Typography 
            variant="body2" 
            color="text.secondary"
            sx={{ 
              fontSize: '0.95rem',
              lineHeight: 1.6,
              mt: 2
            }}
          >
            In case of a refund, visit the{' '}
            <Link 
              to="/faq" 
              onClick={handleCancelModalClose}
              style={{ 
                color: '#5F3C6F',
                textDecoration: 'underline',
                fontWeight: 600
              }}
            >
              FAQs page
            </Link>
            {' '}for terms and conditions.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 4 }}>
          <Button
            variant="outlined"
            onClick={handleCancelModalClose}
            disabled={cancellingTicketId !== null}
            sx={{ 
              px: 4,
              py: 1.5,
              borderRadius: 2,
              textTransform: 'none',
              fontSize: '1.1rem',
              fontWeight: 600,
              mr: 2,
              borderColor: 'primary.main',
              color: 'primary.main',
              '&:hover': {
                borderColor: 'primary.dark',
                color: 'primary.dark',
                backgroundColor: 'rgba(95, 60, 111, 0.04)'
              }
            }}
          >
            Keep
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleCancelBooking}
            disabled={cancellingTicketId !== null}
            sx={{ 
              px: 4,
              py: 1.5,
              borderRadius: 2,
              textTransform: 'none',
              fontSize: '1.1rem',
              fontWeight: 600,
              boxShadow: '0 4px 12px rgba(244, 67, 54, 0.2)',
              '&:hover': {
                boxShadow: '0 6px 16px rgba(244, 67, 54, 0.3)'
              }
            }}
          >
            {cancellingTicketId === bookingToCancel?.id ? 'Cancelling...' : 'Cancel'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MyBookings; 